import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { userAPI, talentAPI, UserTalent, Talent } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import UserBasicInfoForm from './user-dialog/UserBasicInfoForm';
import UserRolesSection from './user-dialog/UserRolesSection';
import UserTalentsSection from './user-dialog/UserTalentsSection';

interface UserDialogProps {
  open: boolean;
  onClose: () => void;
  userId?: number; // undefined = opprett ny
  onSave: () => void;
}

const UserDialog: React.FC<UserDialogProps> = ({ open, onClose, userId: initialUserId, onSave }) => {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Brukerdata
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [originalEmail, setOriginalEmail] = useState(''); // For å detektere endringer
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+47');
  const [isActive, setIsActive] = useState(true);
  const [roles, setRoles] = useState<string[]>(['user']);
  
  // Passordbekreftelse for e-postendring
  const [currentPassword, setCurrentPassword] = useState('');
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [showAdminConfirm, setShowAdminConfirm] = useState(false);

  // Talent-data
  const [userTalents, setUserTalents] = useState<UserTalent[]>([]);
  const [allTalents, setAllTalents] = useState<Talent[]>([]);

  // Tracking av bruker-ID (kan endres fra undefined til number etter opprettelse)
  const [currentUserId, setCurrentUserId] = useState<number | undefined>(initialUserId);
  const isEditMode = currentUserId !== undefined;
  const isAdmin = currentUser?.roles.includes('admin') || false;
  const isOwnProfile = currentUser?.id === currentUserId;

  useEffect(() => {
    if (open) {
      setCurrentUserId(initialUserId);
      fetchData(initialUserId); // Send userId direkte
    } else {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialUserId]);

  const fetchData = async (userId?: number) => {
    try {
      setLoading(true);
      setError(null);

      // Hent alle tilgjengelige talents
      const talentsData = await talentAPI.getAll();
      setAllTalents(talentsData.talenter || []);

      // Hvis edit mode, hent brukerdata og brukerens talents
      if (userId) {
        const [userData, userTalentsData] = await Promise.all([
          userAPI.getById(userId),
          userAPI.getUserTalents(userId),
        ]);

        const user = userData.user;
        setFirstName(user.firstName);
        setLastName(user.lastName);
        setEmail(user.email);
        setOriginalEmail(user.email); // Lagre original e-post
        setPhoneNumber(user.phoneNumber || '');
        setPhoneCountryCode(user.phoneCountryCode || '+47');
        setIsActive(user.isActive ?? true);
        setRoles(user.roles || []);
        setUserTalents(userTalentsData.talents || []);
      }
    } catch (err: any) {
      console.error('Feil ved lasting av data:', err);
      setError('Kunne ikke laste data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCurrentUserId(undefined);
    setFirstName('');
    setLastName('');
    setEmail('');
    setOriginalEmail('');
    setPhoneNumber('');
    setPhoneCountryCode('+47');
    setIsActive(true);
    setRoles(['user']);
    setUserTalents([]);
    setCurrentPassword('');
    setShowPasswordConfirm(false);
    setShowAdminConfirm(false);
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!firstName.trim() || !lastName.trim() || !email.trim() || !phoneNumber.trim()) {
        setError('Fornavn, etternavn, e-post og telefonnummer er påkrevd');
        setLoading(false);
        return;
      }

      if (roles.length === 0) {
        setError('Brukeren må ha minst én rolle');
        setLoading(false);
        return;
      }

      if (isEditMode && currentUserId) {
        // Oppdater eksisterende bruker
        const updateData: any = {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phoneNumber: phoneNumber.trim(),
          phoneCountryCode: phoneCountryCode.trim(),
          roles,
          isActive,
        };
        
        // Hvis e-post er endret, legg til i update
        if (email !== originalEmail) {
          updateData.email = email.trim();
          
          // Hvis passord er påkrevd, legg til
          if (currentPassword) {
            updateData.currentPassword = currentPassword;
          }
        }
        
        await userAPI.update(currentUserId, updateData);
        
        // Oppdater original e-post etter vellykket endring
        if (email !== originalEmail) {
          setOriginalEmail(email);
          setCurrentPassword('');
          setShowPasswordConfirm(false);
          setShowAdminConfirm(false);
        }
        
        setSuccess('Bruker oppdatert! Du kan fortsette å redigere eller lukke dialogen.');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        // Opprett ny bruker
        const result = await userAPI.create({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phoneNumber: phoneNumber.trim(),
          phoneCountryCode: phoneCountryCode.trim(),
          roles,
          isActive,
        });

        // Sett bruker-ID slik at vi går over i edit-mode
        setCurrentUserId(result.user.id);

        // Hvis talents er lagt til lokalt, legg dem til for den nye brukeren
        if (result.user && userTalents.length > 0) {
          // Legg til talents en etter en
          for (const talent of userTalents) {
            try {
              await userAPI.addUserTalent(result.user.id, {
                talentId: talent.talent_id,
                erfaringsnivaa: talent.erfaringsnivaa,
                notater: talent.notater,
              });
            } catch (talentErr: any) {
              console.error('Feil ved legg til talent:', talentErr);
              // Fortsett med neste talent selv om en feiler
            }
          }
        }
        setSuccess('Bruker opprettet! Legg til talents nedenfor.');
        setTimeout(() => setSuccess(null), 4000);
      }

      // Oppdater listen i bakgrunnen uten å lukke dialogen
      onSave();
    } catch (err: any) {
      console.error('Feil ved lagring:', err);
      setError(err.response?.data?.error || 'Kunne ikke lagre bruker');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTalent = async (talent: Talent) => {
    try {
      setError(null);

      // Sjekk om talent allerede er lagt til
      if (userTalents.some(t => t.talent_id === talent.id)) {
        setError('Dette talentet er allerede lagt til');
        setTimeout(() => setError(null), 3000);
        return;
      }

      if (isEditMode && currentUserId) {
        // Hvis edit mode, legg til i backend
        const result = await userAPI.addUserTalent(currentUserId, {
          talentId: talent.id,
          erfaringsnivaa: 'avansert',
        });

        setUserTalents([...userTalents, result.talent]);
      } else {
        // Hvis opprett mode, legg til lokalt (lagres når bruker opprettes)
        const newTalent: UserTalent = {
          id: 0,
          bruker_id: 0,
          talent_id: talent.id,
          talent_navn: talent.navn,
          kategori_id: talent.kategori_id,
          kategori_navn: talent.kategori_navn || '',
          erfaringsnivaa: 'avansert',
          created_at: new Date().toISOString(),
        };
        setUserTalents([...userTalents, newTalent]);
      }
    } catch (err: any) {
      console.error('Feil ved legg til talent:', err);
      setError(err.response?.data?.error || 'Kunne ikke legge til talent');
    }
  };

  const handleUpdateTalentDetail = async (
    talentId: number,
    field: 'erfaringsnivaa' | 'notater',
    value: any
  ) => {
    try {
      const talentIndex = userTalents.findIndex(t => t.talent_id === talentId);
      if (talentIndex === -1) return;

      const updatedTalents = [...userTalents];
      updatedTalents[talentIndex] = {
        ...updatedTalents[talentIndex],
        [field]: value,
      };
      setUserTalents(updatedTalents);

      // Hvis edit mode, oppdater i backend
      if (isEditMode && currentUserId) {
        await userAPI.updateUserTalent(currentUserId, talentId, {
          erfaringsnivaa: updatedTalents[talentIndex].erfaringsnivaa,
          notater: updatedTalents[talentIndex].notater,
        });
      }
    } catch (err: any) {
      console.error('Feil ved oppdatering:', err);
      setError('Kunne ikke oppdatere talent');
    }
  };

  const handleRemoveTalent = async (talentId: number) => {
    try {
      setError(null);

      if (isEditMode && currentUserId) {
        await userAPI.removeUserTalent(currentUserId, talentId);
      }

      setUserTalents(userTalents.filter(t => t.talent_id !== talentId));
    } catch (err: any) {
      console.error('Feil ved fjerning av talent:', err);
      setError(err.response?.data?.error || 'Kunne ikke fjerne talent');
    }
  };

  // Administrator og Bruker er gjensidig utelukkende: valget erstatter alltid forrige rolle.
  const handleRoleSelect = (role: string) => {
    setRoles([role]);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Rediger bruker' : 'Opprett ny bruker'}</DialogTitle>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center justify-between gap-2">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-sm underline">
                Lukk
              </button>
            </AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="border-status-confirmed/40 bg-status-confirmed/10">
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col gap-5">
          {/* Grunnleggende info */}
          <UserBasicInfoForm
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            email={email}
            setEmail={setEmail}
            originalEmail={originalEmail}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            phoneCountryCode={phoneCountryCode}
            setPhoneCountryCode={setPhoneCountryCode}
            isActive={isActive}
            setIsActive={setIsActive}
            loading={loading}
            isEditMode={isEditMode}
            isOwnProfile={isOwnProfile}
            isAdmin={isAdmin}
            showPasswordConfirm={showPasswordConfirm}
            setShowPasswordConfirm={setShowPasswordConfirm}
            showAdminConfirm={showAdminConfirm}
            setShowAdminConfirm={setShowAdminConfirm}
            currentPassword={currentPassword}
            setCurrentPassword={setCurrentPassword}
          />

          <Separator />

          {/* Roller */}
          <UserRolesSection
            roles={roles}
            onRoleSelect={handleRoleSelect}
            loading={loading}
          />

          <Separator />

          {/* Talents */}
          <UserTalentsSection
            userTalents={userTalents}
            allTalents={allTalents}
            onAddTalent={handleAddTalent}
            onUpdateTalentDetail={handleUpdateTalentDetail}
            onRemoveTalent={handleRemoveTalent}
            loading={loading}
          />
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <p className="text-xs text-muted-foreground sm:flex-1">
            {isEditMode ? 'Talents lagres automatisk' : 'Opprett bruker først, deretter legg til talents'}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={loading}>
              {success ? 'Lukk' : 'Avbryt'}
            </Button>
            <Button onClick={handleSave} disabled={loading || (isEditMode && success !== null)}>
              {isEditMode ? 'Lagre endringer' : 'Opprett bruker'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserDialog;
